import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import RoadStatusService from '../services/RoadStatuses';

const STATUS_COLORS = {
  open: { bg: '#198754', text: '#fff' },
  closed: { bg: '#dc3545', text: '#fff' },
  seasonal_closure: { bg: '#fd7e14', text: '#fff' },
  chain_law: { bg: '#ffc107', text: '#000' },
  restrictions: { bg: '#0d6efd', text: '#fff' },
  unknown: { bg: '#6c757d', text: '#fff' },
};

const STATUS_LABELS = {
  open: 'Open',
  closed: 'Closed',
  seasonal_closure: 'Seasonal Closure',
  chain_law: 'Chain Law',
  restrictions: 'Restrictions',
  unknown: 'Unknown',
};

const METHOD_COLORS = {
  OFFICIAL_POST: { bg: '#198754', text: '#fff' },
  GIS_LAYER: { bg: '#0d6efd', text: '#fff' },
  SCHEDULE_BASED: { bg: '#fd7e14', text: '#fff' },
  HISTORICAL_PATTERN: { bg: '#fd7e14', text: '#fff' },
  INFERRED_OPEN: { bg: '#ffc107', text: '#000' },
  INFERRED_CLOSED: { bg: '#ffc107', text: '#000' },
  CONSTRUCTION: { bg: '#6f42c1', text: '#fff' },
  FIRE_CLOSURE: { bg: '#dc3545', text: '#fff' },
  WEATHER_EVENT: { bg: '#dc3545', text: '#fff' },
  PERMIT_REQUIRED: { bg: '#0dcaf0', text: '#000' },
  UNKNOWN: { bg: '#6c757d', text: '#fff' },
};

const AGENCY_COLORS = {
  CDOT: '#198754',
  USFS: '#795548',
  BLM: '#ff9800',
  NPS: '#2e7d32',
  CPW: '#1565c0',
  COUNTY: '#5c6bc0',
  MUNICIPAL: '#7e57c2',
  DENVER_WATER: '#0288d1',
  MILITARY: '#455a64',
  STATE_LAND_BOARD: '#8d6e63',
  PRIVATE: '#78909c',
  MULTI_JURISDICTION: '#9e9e9e',
};

function StatusBadge({ status, reason }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span style={{
      backgroundColor: colors.bg,
      color: colors.text,
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '0.8em',
      fontWeight: 'bold',
      display: 'inline-block',
      cursor: reason ? 'help' : 'default',
    }}
    title={reason || ''}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function MethodBadge({ method, detail }) {
  if (!method) return <span style={{ color: '#999', fontSize: '0.8em' }}>Not Available</span>;
  const colors = METHOD_COLORS[method] || METHOD_COLORS.UNKNOWN;
  return (
    <div>
      <span style={{
        backgroundColor: colors.bg,
        color: colors.text,
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.7em',
        fontWeight: 'bold',
        display: 'inline-block',
        marginBottom: '2px',
      }}
      title={detail || ''}>
        {method}
      </span>
      {detail && (
        <div style={{ fontSize: '0.75em', color: '#666', lineHeight: '1.2', marginTop: '2px' }}>
          {detail.length > 80 ? detail.substring(0, 80) + '...' : detail}
        </div>
      )}
    </div>
  );
}

function AgencyBadge({ agencyType, office, subarea }) {
  if (!agencyType) return <span style={{ color: '#999', fontSize: '0.8em' }}>Not Available</span>;
  const color = AGENCY_COLORS[agencyType] || '#6c757d';
  return (
    <div>
      <span style={{
        backgroundColor: color,
        color: '#fff',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.7em',
        fontWeight: 'bold',
        display: 'inline-block',
      }}>
        {agencyType}
      </span>
      {office && (
        <div style={{ fontSize: '0.8em', color: '#333', lineHeight: '1.2', marginTop: '2px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          {office}
        </div>
      )}
      {subarea && (
        <div style={{ fontSize: '0.75em', color: '#666', lineHeight: '1.2', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
          {subarea}
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

function DataFreshness({ fetchedAt }) {
  if (!fetchedAt) return <span style={{ fontSize: '0.8em', color: '#999' }}>Pending</span>;
  const now = new Date();
  const fetched = new Date(fetchedAt);
  const hoursAgo = (now - fetched) / (1000 * 60 * 60);
  let color = '#198754';
  let label = 'Fresh';
  if (hoursAgo > 72) { color = '#dc3545'; label = 'Stale'; }
  else if (hoursAgo > 24) { color = '#ffc107'; label = 'Aging'; }
  return (
    <div>
      <span style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
        marginRight: '4px',
      }} title={label} />
      <span style={{ fontSize: '0.8em' }}>{formatDate(fetchedAt)}</span>
    </div>
  );
}

function MapLink({ road }) {
  // Tier 1: Direct map URL from source
  if (road.map_url) {
    return (
      <a href={road.map_url} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: '0.8em' }} title="Source map">
        Map
      </a>
    );
  }
  // Tier 2: Lat/lon coordinates
  if (road.lat && road.long) {
    const url = `https://www.google.com/maps?q=${road.lat},${road.long}`;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: '0.8em' }} title={`${road.lat}, ${road.long}`}>
        Map
      </a>
    );
  }
  // Tier 3: Search link using road name + region
  if (road.name) {
    const searchTerms = [road.name, road.region || road.managing_office, 'Colorado'].filter(Boolean).join('+');
    const url = `https://www.google.com/maps/search/${encodeURIComponent(searchTerms)}`;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: '0.8em', color: '#6c757d' }} title="Search-based link">
        Search
      </a>
    );
  }
  // Tier 4: Source URL
  if (road.source_url) {
    return (
      <a href={road.source_url} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: '0.8em', color: '#6c757d' }} title="Agency page">
        Source
      </a>
    );
  }
  // No map available
  return (
    <span style={{
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.7em',
    }}>
      No Map
    </span>
  );
}

function ExpandableText({ text, maxLength = 150 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#999', fontSize: '0.85em' }}>—</span>;
  if (text.length <= maxLength) {
    return <span style={{ fontSize: '0.85em' }}>{text}</span>;
  }
  return (
    <div style={{ fontSize: '0.85em' }}>
      {expanded ? text : text.substring(0, maxLength) + '...'}
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{
          background: 'none',
          border: 'none',
          color: '#0d6efd',
          fontSize: '0.85em',
          cursor: 'pointer',
          padding: '0 4px',
          textDecoration: 'underline',
        }}>
        {expanded ? 'Less' : 'More'}
      </button>
    </div>
  );
}

function SourcesBadge({ count }) {
  if (!count || count <= 1) return null;
  return (
    <span style={{
      backgroundColor: '#6c757d',
      color: '#fff',
      padding: '1px 5px',
      borderRadius: '8px',
      fontSize: '0.65em',
      fontWeight: 'bold',
      marginLeft: '4px',
    }} title={`${count} data sources reference this road`}>
      {count}
    </span>
  );
}

const CELL_STYLE = {
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const RoadStatus = () => {
  const [roads, setRoads] = useState([]);
  const [regions, setRegions] = useState([]);
  const [roadTypes, setRoadTypes] = useState([]);
  const [agencyTypes, setAgencyTypes] = useState([]);
  const [sourceMethods, setSourceMethods] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filters, setFilters] = useState({
    status: '', region: '', road_type: '', agency_type: '', status_source_method: '', search: ''
  });
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [changes, setChanges] = useState([]);
  const [seasonalProfile, setSeasonalProfile] = useState(null);
  const [sourceAttributions, setSourceAttributions] = useState([]);
  const [hasConflict, setHasConflict] = useState(false);
  const [showHealth, setShowHealth] = useState(false);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineRunning, setPipelineRunning] = useState(false);

  function loadRoads() {
    setLoading(true);
    RoadStatusService.getRoadStatuses(filters).then((resp) => {
      setRoads(resp.roads || []);
      setRegions(resp.regions || []);
      setRoadTypes(resp.road_types || []);
      setAgencyTypes(resp.agency_types || []);
      setSourceMethods(resp.source_methods || []);
      setStatuses(resp.statuses || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  function loadHealth() {
    RoadStatusService.getPipelineHealth().then((resp) => {
      setHealth(resp);
    });
  }

  useEffect(() => {
    loadRoads();
  }, [filters.status, filters.region, filters.road_type, filters.agency_type, filters.status_source_method]);

  function handleSearch() {
    loadRoads();
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function sortedRoads() {
    return [...roads].sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function selectRoad(road) {
    if (selectedRoad && selectedRoad.id === road.id) {
      setSelectedRoad(null);
      setChanges([]);
      setSeasonalProfile(null);
      setSourceAttributions([]);
      setHasConflict(false);
      return;
    }
    setSelectedRoad(road);
    RoadStatusService.getRoadChanges(road.id).then((resp) => {
      setChanges(resp.changes || []);
      setSeasonalProfile(resp.seasonal_profile || null);
      setSourceAttributions(resp.source_attributions || []);
      setHasConflict(resp.has_status_conflict || false);
    });
  }

  function exportCSV() {
    const rows = sortedRoads();
    const headers = [
      'Name', 'Type', 'Status', 'Status Source Method', 'Agency Type', 'Managing Office',
      'Jurisdiction Sub-Area', 'Region', 'Elevation', 'Lat', 'Long',
      'Status Reason', 'Detail', 'Source Dataset', 'Source Data Format',
      'Source URL', 'Last Updated', 'Sources Count',
    ];
    const csvRows = [headers.join(',')];
    rows.forEach(r => {
      const q = (v) => '"' + (v || '').toString().replace(/"/g, '""') + '"';
      const row = [
        q(r.name), q(r.road_type), r.status || 'unknown', q(r.status_source_method),
        q(r.agency_type), q(r.managing_office), q(r.jurisdiction_subarea), q(r.region),
        r.elevation || '', r.lat || '', r.long || '',
        q(r.status_reason), q(r.status_detail), q(r.source_dataset_name), q(r.source_data_format),
        q(r.source_url), r.fetched_at ? new Date(r.fetched_at).toLocaleString() : '',
        r.source_count || 0,
      ];
      csvRows.push(row.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'road_status_' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportGeoJSON() {
    window.open('/road_statuses/geojson', '_blank');
  }

  function runPipeline() {
    setPipelineRunning(true);
    RoadStatusService.runPipeline().then(() => {
      setTimeout(() => {
        setPipelineRunning(false);
        loadRoads();
        if (showHealth) loadHealth();
      }, 3000);
    });
  }

  function toggleHealth() {
    if (!showHealth) loadHealth();
    setShowHealth(!showHealth);
  }

  function sortArrow(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="m-5">
      <div className="container-fluid">
        <BackButton />
        <h2 className="text-center">Road & Pass Status</h2>

        {/* Filters */}
        <div className="row mb-3 g-2">
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              {statuses.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.agency_type}
              onChange={(e) => setFilters({ ...filters, agency_type: e.target.value })}>
              <option value="">All Agencies</option>
              {agencyTypes.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}>
              <option value="">All Regions</option>
              {regions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.status_source_method}
              onChange={(e) => setFilters({ ...filters, status_source_method: e.target.value })}>
              <option value="">All Methods</option>
              {sourceMethods.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <div className="input-group input-group-sm">
              <input type="text" className="form-control" placeholder="Search roads, offices..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-outline-secondary" onClick={handleSearch}>Search</button>
            </div>
          </div>
          <div className="col-md-1">
            <button className="btn btn-sm btn-outline-primary w-100" onClick={toggleHealth}>
              {showHealth ? 'Hide' : 'Health'}
            </button>
          </div>
        </div>

        {/* Pipeline Actions */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <span className="text-muted">
            {loading ? 'Loading...' : `${roads.length} roads`}
          </span>
          <div>
            <button className="btn btn-sm btn-outline-secondary me-2" onClick={exportCSV}>
              Export CSV
            </button>
            <button className="btn btn-sm btn-outline-info me-2" onClick={exportGeoJSON}
              title="Download GeoJSON for ArcGIS (roads with coordinates only)">
              Export GeoJSON
            </button>
            <a href="/road_statuses/shapefile" className="btn btn-sm btn-outline-warning me-2"
              title="Download Shapefile (.shp/.shx/.dbf/.prj) for ArcGIS/QGIS">
              Export Shapefile
            </a>
            <button className="btn btn-sm btn-success" onClick={runPipeline} disabled={pipelineRunning}>
              {pipelineRunning ? 'Running Pipeline...' : 'Run Pipeline Now'}
            </button>
          </div>
        </div>

        {/* Pipeline Health Panel */}
        {showHealth && health && (
          <div className="card mb-3">
            <div className="card-header">
              <strong>Pipeline Health</strong>
              <span className="ms-3 text-muted">
                {health.total_roads} roads total | {health.roads_with_status} with status | {health.roads_without_status} pending
              </span>
            </div>
            <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {health.last_batch && (
                <div className="mb-2">
                  <strong>Last batch:</strong>{' '}
                  <span className={health.last_batch.status === 'completed' ? 'text-success' : 'text-danger'}>
                    {health.last_batch.status}
                  </span>
                  {' | '}
                  {health.last_batch.successful_sources}/{health.last_batch.total_sources} sources
                  {' | '}
                  {health.last_batch.total_records} records
                  {' | '}
                  {formatDate(health.last_batch.finished_at)}
                </div>
              )}
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Source</th>
                    <th>Type</th>
                    <th>Enabled</th>
                    <th>Last Status</th>
                    <th>Last Run</th>
                    <th>Duration</th>
                    <th>Records</th>
                    <th>Failures (7d)</th>
                  </tr>
                </thead>
                <tbody>
                  {(health.source_health || []).map((s) => (
                    <tr key={s.id} className={!s.enabled ? 'text-muted' : ''}>
                      <td>{s.name}</td>
                      <td>{s.source_type}</td>
                      <td>{s.enabled ? 'Yes' : 'No'}</td>
                      <td>
                        {s.last_status === 'success' && <span className="text-success">OK</span>}
                        {s.last_status === 'failed' && (
                          <span className="text-danger" title={s.last_error}>FAIL</span>
                        )}
                        {!s.last_status && <span className="text-muted">Never</span>}
                      </td>
                      <td>{formatDate(s.last_run_at)}</td>
                      <td>{s.last_duration_ms ? `${s.last_duration_ms}ms` : '—'}</td>
                      <td>{s.last_records != null ? s.last_records : '—'}</td>
                      <td className={s.failures_7d > 3 ? 'text-danger fw-bold' : ''}>
                        {s.failures_7d}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Road Status Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-hover table-bordered table-striped" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '180px' }} />  {/* Name */}
              <col style={{ width: '70px' }} />   {/* Type */}
              <col style={{ width: '95px' }} />   {/* Status */}
              <col style={{ width: '140px' }} />  {/* Method */}
              <col style={{ width: '150px' }} />  {/* Agency/Office */}
              <col style={{ width: '250px' }} />  {/* Detail */}
              <col style={{ width: '130px' }} />  {/* Source */}
              <col style={{ width: '50px' }} />   {/* Map */}
              <col style={{ width: '120px' }} />  {/* Updated */}
            </colgroup>
            <thead className="table-secondary">
              <tr>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('name')}>
                  Name{sortArrow('name')}
                </th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('road_type')}>
                  Type{sortArrow('road_type')}
                </th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('status')}>
                  Status{sortArrow('status')}
                </th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('status_source_method')}>
                  Method{sortArrow('status_source_method')}
                </th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('agency_type')}>
                  Agency / Office{sortArrow('agency_type')}
                </th>
                <th style={CELL_STYLE}>Detail</th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('source_dataset_name')}>
                  Source{sortArrow('source_dataset_name')}
                </th>
                <th style={CELL_STYLE}>Map</th>
                <th style={{ cursor: 'pointer', ...CELL_STYLE }} onClick={() => handleSort('fetched_at')}>
                  Updated{sortArrow('fetched_at')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRoads().map((road) => (
                <React.Fragment key={road.id}>
                  <tr onClick={() => selectRoad(road)}
                    style={{ cursor: 'pointer' }}
                    className={selectedRoad && selectedRoad.id === road.id ? 'table-active' : ''}>
                    <td style={CELL_STYLE}>
                      <strong>{road.name}</strong>
                      {road.has_seasonal_profile && (
                        <span title="Seasonal profile available" style={{ marginLeft: '4px', fontSize: '0.8em' }}>&#10052;</span>
                      )}
                      <SourcesBadge count={road.source_count} />
                    </td>
                    <td style={{ ...CELL_STYLE, fontSize: '0.85em' }}>{road.road_type || '—'}</td>
                    <td style={CELL_STYLE}>
                      <StatusBadge status={road.status || 'unknown'} reason={road.status_reason} />
                    </td>
                    <td style={CELL_STYLE}>
                      <MethodBadge method={road.status_source_method} detail={road.status_method_detail} />
                    </td>
                    <td style={CELL_STYLE}>
                      <AgencyBadge agencyType={road.agency_type} office={road.managing_office} subarea={road.jurisdiction_subarea} />
                    </td>
                    <td style={CELL_STYLE}>
                      <ExpandableText text={road.status_detail} maxLength={120} />
                    </td>
                    <td style={CELL_STYLE}>
                      <span style={{ fontSize: '0.8em' }}>{road.source_dataset_name || road.source_name || '—'}</span>
                      {road.source_data_format && (
                        <div style={{ fontSize: '0.7em', color: '#999' }}>{road.source_data_format}</div>
                      )}
                    </td>
                    <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                      <MapLink road={road} />
                    </td>
                    <td style={CELL_STYLE}>
                      <DataFreshness fetchedAt={road.fetched_at} />
                    </td>
                  </tr>

                  {/* Expanded Detail Panel */}
                  {selectedRoad && selectedRoad.id === road.id && (
                    <tr>
                      <td colSpan="9" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="p-3">
                          {/* Row 1: Road Info + Attribution */}
                          <div className="row mb-3">
                            <div className="col-md-4">
                              <h6 className="mb-2">{road.name}</h6>
                              {road.description && (
                                <p className="text-muted mb-2" style={{ fontSize: '0.9em', wordWrap: 'break-word' }}>
                                  {road.description}
                                </p>
                              )}
                              {road.elevation && (
                                <div className="mb-1" style={{ fontSize: '0.85em' }}>
                                  <strong>Elevation:</strong> {road.elevation.toLocaleString()}'
                                </div>
                              )}
                              {road.lat && road.long && (
                                <div className="mb-1" style={{ fontSize: '0.85em' }}>
                                  <strong>Coordinates:</strong>{' '}
                                  <a href={`https://www.google.com/maps?q=${road.lat},${road.long}`}
                                    target="_blank" rel="noopener noreferrer">
                                    {road.lat}, {road.long}
                                  </a>
                                </div>
                              )}
                              {road.status_reason && (
                                <div className="mb-1" style={{ fontSize: '0.85em' }}>
                                  <strong>Status Reason:</strong>{' '}
                                  <span className="text-muted" style={{ wordWrap: 'break-word' }}>{road.status_reason}</span>
                                </div>
                              )}

                              {/* Status Conflict Warning */}
                              {hasConflict && (
                                <div className="alert alert-warning py-1 px-2 mt-2" style={{ fontSize: '0.85em' }}>
                                  <strong>STATUS CONFLICT:</strong> Multiple sources report different statuses for this road.
                                </div>
                              )}
                            </div>

                            {/* Source Attribution */}
                            <div className="col-md-4">
                              <h6 className="mb-2" style={{ fontSize: '0.9em' }}>Source Attribution</h6>
                              <table className="table table-sm mb-0" style={{ fontSize: '0.8em' }}>
                                <tbody>
                                  <tr>
                                    <td className="fw-bold" style={{ width: '120px' }}>Dataset</td>
                                    <td style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>{road.source_dataset_name || 'Not Available'}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Format</td>
                                    <td>{road.source_data_format || 'Not Available'}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Source URL</td>
                                    <td style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                      {road.source_direct_url ? (
                                        <a href={road.source_direct_url} target="_blank" rel="noopener noreferrer">{road.source_direct_url}</a>
                                      ) : road.source_url ? (
                                        <a href={road.source_url} target="_blank" rel="noopener noreferrer">{road.source_url}</a>
                                      ) : 'Not Available'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Source Updated</td>
                                    <td>{road.source_last_updated ? formatDate(road.source_last_updated) : 'Not Available'}</td>
                                  </tr>
                                  <tr>
                                    <td className="fw-bold">Last Scraped</td>
                                    <td>{formatDate(road.fetched_at)}</td>
                                  </tr>
                                </tbody>
                              </table>

                              {/* Multi-source Attributions */}
                              {sourceAttributions.length > 1 && (
                                <div className="mt-2">
                                  <strong style={{ fontSize: '0.8em' }}>All Source Reports:</strong>
                                  <table className="table table-sm table-bordered mt-1 mb-0" style={{ fontSize: '0.75em' }}>
                                    <thead>
                                      <tr><th>Source</th><th>Status</th><th>Method</th><th>Fetched</th></tr>
                                    </thead>
                                    <tbody>
                                      {sourceAttributions.map((a, idx) => (
                                        <tr key={idx} style={hasConflict && a.status !== road.status ? { backgroundColor: '#fff3cd' } : {}}>
                                          <td style={{ wordWrap: 'break-word' }}>{a.source_name}</td>
                                          <td><StatusBadge status={a.status || 'unknown'} /></td>
                                          <td>{a.status_source_method || '—'}</td>
                                          <td>{formatDate(a.fetched_at)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Seasonal Profile */}
                            <div className="col-md-4">
                              <h6 className="mb-2" style={{ fontSize: '0.9em' }}>
                                Seasonal Profile
                                {!seasonalProfile && <span className="text-muted fw-normal"> — No profile available</span>}
                              </h6>
                              {seasonalProfile && (
                                <table className="table table-sm mb-0" style={{ fontSize: '0.8em' }}>
                                  <tbody>
                                    <tr>
                                      <td className="fw-bold" style={{ width: '110px' }}>Open Season</td>
                                      <td style={{ wordWrap: 'break-word' }}>{seasonalProfile.typical_open_season || 'Not Available'}</td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">Close Season</td>
                                      <td style={{ wordWrap: 'break-word' }}>{seasonalProfile.typical_close_season || 'Not Available'}</td>
                                    </tr>
                                    <tr>
                                      <td className="fw-bold">Authority</td>
                                      <td style={{ wordWrap: 'break-word' }}>{seasonalProfile.closure_authority || 'Not Available'}</td>
                                    </tr>
                                    {seasonalProfile.closure_history && (
                                      <tr>
                                        <td className="fw-bold">History</td>
                                        <td style={{ wordWrap: 'break-word' }}>{seasonalProfile.closure_history}</td>
                                      </tr>
                                    )}
                                    {seasonalProfile.seasonal_notes && (
                                      <tr>
                                        <td className="fw-bold">Notes</td>
                                        <td style={{ wordWrap: 'break-word' }}>{seasonalProfile.seasonal_notes}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>

                          {/* Row 2: Change History */}
                          <h6 style={{ fontSize: '0.9em' }}>Change History</h6>
                          {changes.length === 0 ? (
                            <p className="text-muted mb-0">No changes recorded yet.</p>
                          ) : (
                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                              <table className="table table-sm table-bordered mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: '140px' }}>Date</th>
                                    <th style={{ width: '90px' }}>From</th>
                                    <th style={{ width: '90px' }}>To</th>
                                    <th style={{ width: '150px' }}>Source</th>
                                    <th>Detail</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {changes.map((c, idx) => (
                                    <tr key={idx}>
                                      <td style={{ fontSize: '0.85em' }}>{formatDate(c.changed_at)}</td>
                                      <td>{c.old_status ? <StatusBadge status={c.old_status} /> : '—'}</td>
                                      <td><StatusBadge status={c.new_status} /></td>
                                      <td style={{ fontSize: '0.85em', ...CELL_STYLE }}>{c.source_name}</td>
                                      <td style={CELL_STYLE}>
                                        <ExpandableText text={c.new_detail} maxLength={200} />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RoadStatus;
